import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './components/auth/AuthProvider';
import { LoginPage } from './components/auth/LoginPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { CustomerList } from './components/customers/CustomerList';
import { CustomerProfile } from './components/customers/CustomerProfile';
import { CustomerDashboard } from './components/customers/CustomerDashboard';
import { useAuth } from './hooks/useAuth';
import Header from './components/layout/Header';
import { initializeCsrf } from './utils/csrf';

// Lazy load invoice components
const InvoiceDashboard = lazy(() => import('./components/admin/invoices/InvoiceDashboard'));
const InvoiceList = lazy(() => import('./components/admin/invoices/InvoiceList'));
const InvoiceFormPayPal = lazy(() => import('./components/admin/invoices/InvoiceFormPayPal'));

// Lazy load calendar component
const CalendarPage = lazy(() => import('./components/calendar/CalendarPage'));

// Lazy load trucks component
const TruckList = lazy(() => import('./components/trucks/TruckList').then(module => ({ default: module.TruckList })));

// Layout Components
const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="min-h-screen bg-surface">
      <Header />
      <main className="container mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
};

// Protected Route Wrapper with optional role check
const ProtectedRoute = ({
  children,
  requireAdmin = false
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
}) => {
  const { isAuthenticated, isLoading, hasCheckedSession, user } = useAuth();

  // Show loading until initial session check completes
  // This prevents redirect during React Strict Mode double-invocation
  if (isLoading || !hasCheckedSession) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-on-surface">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If admin is required but user is not admin, redirect to customer dashboard
  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/customer/dashboard" replace />;
  }

  return <>{children}</>;
};

// 404 Page
const NotFoundPage = () => (
  <div className="text-center py-12">
    <h2 className="text-4xl font-bold text-on-surface mb-4">404</h2>
    <p className="text-on-surface-variant">Page not found</p>
  </div>
);

// Root Route Redirect based on user role
const RootRedirect = () => {
  const { isAuthenticated, isLoading, hasCheckedSession, user } = useAuth();

  // Wait for initial session check to complete
  if (isLoading || !hasCheckedSession) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-on-surface">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on user role
  if (user?.role === 'admin') {
    return <Navigate to="/customers" replace />;
  }

  return <Navigate to="/customer/dashboard" replace />;
};

// Main App Component
function App() {
  // Initialize CSRF protection on app mount
  useEffect(() => {
    initializeCsrf().catch((error) => {
      console.error('Failed to initialize CSRF protection:', error);
      // Don't block app - CSRF will be fetched on first state-changing request
    });
  }, []);

  return (
    <AuthProvider>
      <Layout>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/" element={<RootRedirect />} />
          <Route path="/dashboard" element={
            <ProtectedRoute requireAdmin>
              <CustomerList />
            </ProtectedRoute>
          } />
          <Route path="/customers" element={
            <ProtectedRoute requireAdmin>
              <CustomerList />
            </ProtectedRoute>
          } />
          <Route path="/customers/:id" element={
            <ProtectedRoute requireAdmin>
              <CustomerProfile />
            </ProtectedRoute>
          } />
          <Route path="/customer/dashboard" element={
            <ProtectedRoute>
              <CustomerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/invoices" element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<div className="flex justify-center py-12">Loading...</div>}>
                <InvoiceList />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/admin/invoices/dashboard" element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<div className="flex justify-center py-12">Loading...</div>}>
                <InvoiceDashboard />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/admin/invoices/create" element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<div className="flex justify-center py-12">Loading...</div>}>
                <InvoiceFormPayPal />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/admin/invoices/:id" element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<div className="flex justify-center py-12">Loading...</div>}>
                <InvoiceFormPayPal />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/admin/calendar" element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<div className="flex justify-center py-12">Loading...</div>}>
                <CalendarPage />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/admin/trucks" element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<div className="flex justify-center py-12">Loading...</div>}>
                <TruckList />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Layout>
    </AuthProvider>
  );
}

export default App;
